import {
  BRIDGE_V2_NS,
  BridgeError,
  CORE_GET_APP_INFO,
  ERROR_TIMEOUT,
  isBridgeV2Message,
  KIND_EVENT,
  newBridgeV2Id,
  type AppInfoPayload,
  type BridgeV2Event,
  type BridgeV2Message,
  type BridgeV2Response,
  type Capabilities,
} from '@/pwa/bridge-v2/protocol';
import { buildCore, type CoreSurface } from './core';
import { buildPush, type PushSurface } from './plugins/push';
import { buildCamera, type CameraSurface } from './plugins/camera';
import { createPluginProxy, type PluginProxy } from './plugins/proxy';

// =============================================================================
// Appbuild Bridge SDK — core Bridge class
// =============================================================================
// Customer-facing wrapper over the appbuild.bridge wire protocol. Provides
// the three primitives the README §4 contract commits to:
//
//   bridge.request(method, payload?)   // Promise<response.payload>; rejects
//                                      //   with BridgeError on ok:false
//   bridge.on(type, callback)          // returns cleanup function
//   bridge.emit(type, payload?)        // fire-and-forget event to host
//
// Plus passive state accessors:
//   bridge.appInfo                     // AppInfoPayload | null
//   bridge.capabilities                // derived from appInfo
//   bridge.ready                       // Promise<AppInfoPayload>
//
// Listener is installed at module load (singleton); customers who bind after
// the page-load appInfo broadcast still get the snapshot via a fallback
// Core.getAppInfo request fired ~250ms after construction.
//
// First-class typed surfaces (bridge.core / push / camera) and the plugin
// proxy live in separate modules layered on top of this class — M2 / M3.

const DEFAULT_CLIENT_TIMEOUT_MS = 25_000;
const READY_FALLBACK_MS = 250;
/** SDK-side error code raised when a response arrives with ok:false but
 *  no `error` object (host bug). Distinct from protocol error codes so
 *  customers can spot it for what it is. */
const ERROR_INVALID_RESPONSE = 'INVALID_RESPONSE';

type EventCallback<T = unknown> = (payload: T) => void;
type Unsubscribe = () => void;

interface PendingRequest {
  resolve: (payload: unknown) => void;
  reject: (err: BridgeError) => void;
  timer: ReturnType<typeof setTimeout>;
  method: string;
}

export interface RequestOptions {
  /** Per-call client-side timeout override (ms). Falls below the host's
   *  default 30s so we generally fail locally first. */
  timeoutMs?: number;
}

export class Bridge {
  private pending = new Map<string, PendingRequest>();
  private listeners = new Map<string, Set<EventCallback>>();
  private _appInfo: AppInfoPayload | null = null;
  private readyResolve!: (info: AppInfoPayload) => void;
  private readonly _ready: Promise<AppInfoPayload>;
  private readyFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  /** Bridge-meta surface (getAppInfo). See src/sdk/core.ts. */
  readonly core: CoreSurface;
  /** First-class plugin surfaces — thin typed wrappers over `request`
   *  that use the named method constants. Customers who prefer raw access
   *  still have `bridge.request(...)`. Second-class plugins use
   *  `bridge.plugin('Name')` — see below. */
  readonly push: PushSurface;
  readonly camera: CameraSurface;

  constructor() {
    this._ready = new Promise<AppInfoPayload>((resolve) => {
      this.readyResolve = resolve;
    });

    this.core = buildCore(this);
    this.push = buildPush(this);
    this.camera = buildCamera(this);

    if (typeof window === 'undefined') {
      // Non-browser context (SSR, tests without a DOM). Nothing to wire;
      // request() will still queue but post() will no-op.
      return;
    }

    window.addEventListener('message', this.handleMessage);

    // The host normally broadcasts appInfo on page load, but pages binding
    // after that point would miss it. Send an explicit Core.getAppInfo
    // shortly after construction if we haven't seen one — best-effort, no
    // throw on failure (the customer's first request will surface any
    // real host-absence problem with a clean timeout).
    this.readyFallbackTimer = setTimeout(() => {
      this.readyFallbackTimer = null;
      if (this._appInfo) return;
      this.request<AppInfoPayload>(CORE_GET_APP_INFO).catch(() => { /* ignore */ });
    }, READY_FALLBACK_MS);
  }

  /** Latest appInfo snapshot received from the host, or null until the
   *  first one arrives. */
  get appInfo(): AppInfoPayload | null {
    return this._appInfo;
  }

  /** Capability snapshot derived from the cached appInfo. Null until ready. */
  get capabilities(): Capabilities | null {
    return this._appInfo?.capabilities ?? null;
  }

  /** Resolves with the first appInfo payload received from the host. */
  get ready(): Promise<AppInfoPayload> {
    return this._ready;
  }

  /**
   * Send a request envelope to the host. Resolves with the response's
   * `payload`, or rejects with a BridgeError carrying the response's
   * `error.code` / `error.message` / `error.data` (e.g. USER_CANCELLED,
   * PLUGIN_NOT_AVAILABLE, INTERNAL_ERROR with plugin-specific data
   * preserved). Rejects with `TIMEOUT` if the client-side timer fires
   * before a response arrives.
   */
  request<T = unknown>(
    method: string,
    payload?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    const id = newBridgeV2Id();
    const timeoutMs = options?.timeoutMs ?? DEFAULT_CLIENT_TIMEOUT_MS;

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new BridgeError(
          ERROR_TIMEOUT,
          `Bridge request '${method}' timed out client-side after ${timeoutMs}ms`,
        ));
      }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as (p: unknown) => void,
        reject,
        timer,
        method,
      });

      this.post({
        ns: BRIDGE_V2_NS,
        method,
        payload,
        id,
      });
    });
  }

  /**
   * Subscribe to a bridge event by type. Returns an unsubscribe function.
   * Multiple subscribers per type are supported and called in
   * subscription order. The 'appInfo' type fires whenever a fresh
   * snapshot arrives.
   */
  on<T = unknown>(type: string, callback: EventCallback<T>): Unsubscribe {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const wrapped = callback as EventCallback;
    set.add(wrapped);
    return () => {
      const s = this.listeners.get(type);
      if (!s) return;
      s.delete(wrapped);
      if (s.size === 0) this.listeners.delete(type);
    };
  }

  /**
   * Returns a Proxy for a second-class Capacitor plugin. Property access
   * becomes a `plugin:<name>.<method>(...args)` bridge request. Function
   * arguments at any depth are wired up as callback markers so the
   * customer's function fires when the host invokes the reconstructed
   * callback natively. `addListener`-style methods return a real
   * `PluginListenerHandle` with a working `.remove()`.
   *
   * Plugin name must be whitelisted in featureMap.ts SECOND_CLASS_PLUGIN_NAMES
   * AND registered with Capacitor in the host shell — otherwise calls
   * resolve to PLUGIN_NOT_AVAILABLE.
   *
   * @example
   *   const Shake = bridge.plugin('CapacitorShake');
   *   const handle = await Shake.addListener('shake', () => console.log('shake!'));
   *   await handle.remove();
   */
  plugin(name: string): PluginProxy {
    return createPluginProxy(name, this);
  }

  /**
   * Fire-and-forget event into the host. The host does not reply.
   * Used for telemetry / instrumentation patterns; most customers won't
   * call this directly.
   */
  emit<T = unknown>(type: string, payload?: T): void {
    const envelope: BridgeV2Event<T> = {
      ns: BRIDGE_V2_NS,
      kind: KIND_EVENT,
      type,
      payload,
    };
    this.post(envelope);
  }

  // ----- Internal --------------------------------------------------------

  private handleMessage = (e: MessageEvent): void => {
    if (!isBridgeV2Message(e.data)) return;
    const msg = e.data;
    // Requests carry no `kind` (page→host). Anything from host→page is
    // the tagged union; ignore stray requests we somehow see in our own
    // inbox (loopback echo, etc.).
    if (!('kind' in msg)) return;

    switch (msg.kind) {
      case 'appInfo':
        this.onAppInfo(msg.payload);
        break;
      case 'event':
        this.dispatch(msg.type, msg.payload);
        break;
      case 'response':
        this.onResponse(msg);
        break;
    }
  };

  private onAppInfo(payload: AppInfoPayload): void {
    this._appInfo = payload;
    this.readyResolve(payload);
    if (this.readyFallbackTimer) {
      clearTimeout(this.readyFallbackTimer);
      this.readyFallbackTimer = null;
    }
    // Also surface as an event so customers can subscribe to refreshes
    // (e.g. after a permission grant). The host doesn't currently
    // re-broadcast on capability change — see BACKLOG — but customers
    // who call Core.getAppInfo themselves still want this hook.
    this.dispatch('appInfo', payload);
  }

  private onResponse(msg: BridgeV2Response): void {
    const id = msg.inReplyTo;
    if (!id) return; // uncorrelated; nothing to do
    const pending = this.pending.get(id);
    if (!pending) return; // unknown id (already timed out, etc.)
    this.pending.delete(id);
    clearTimeout(pending.timer);

    if (msg.ok) {
      pending.resolve(msg.payload);
    } else if (msg.error) {
      pending.reject(new BridgeError(msg.error.code, msg.error.message, msg.error.data));
    } else {
      pending.reject(new BridgeError(
        ERROR_INVALID_RESPONSE,
        `Response for '${pending.method}' had ok:false but no error object`,
      ));
    }
  }

  private dispatch(type: string, payload: unknown): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (err) {
        console.error(`[AppbuildWrapper] listener for '${type}' threw:`, err);
      }
    }
  }

  private post(envelope: BridgeV2Message): void {
    if (typeof window === 'undefined') return;
    try {
      // window.parent reaches the host iframe parent in PWA embed mode.
      // In the native Capgo InAppBrowser, window.parent === window and
      // the injected transport shim re-routes same-window posts to the
      // native shell. Same pattern the existing bridge tester uses.
      window.parent.postMessage(envelope, '*');
    } catch (err) {
      console.error('[AppbuildWrapper] post failed:', err);
    }
  }
}

/** Singleton instance — the IIFE entry assigns this to window.AppbuildWrapper.
 *  Bundled consumers can import { bridge } from '@/snippets/appbuild-wrapper-sdk' directly. */
export const bridge = new Bridge();
